import { useCallback, useEffect, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

import { useMutation, useQuery } from '@apollo/client/react'
import { clsx } from 'clsx'

import {
  Button,
  CheckIcon,
  Collapsible,
  CopyIcon,
  Flex,
  Heading,
  IconButton,
  Notice,
  PromotionalBanner,
  SendHorizontalIcon,
  Share2Icon,
  Skeleton,
  Text,
  toast,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import referAFriendStyle from './ReferAFriend.module.css'
import style from '../Settings.module.css'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

export const ReferralProgramStatusQuery = graphql(/* GraphQL */ `
  query referralProgramStatus($input: ReferralProgramStatusInput!) {
    referralProgramStatus(input: $input) {
      __typename
      ... on ReferralProgramUnavailable {
        message
      }
      ... on ReferralProgramUnenrolled {
        terms {
          policyVersion
          maxRewards
          refereeTrialDays
          rewardAmountInMinorUnits
          rewardCurrency
        }
      }
      ... on ReferralProgramEnrolled {
        referralUrl
        sharingStatus
        rewardSummary {
          grantedCount
          inProgressCount
          nextExpiryAt
          reconcilingCount
          remainingCount
          waitingForBillingCount
        }
        terms {
          policyVersion
          maxRewards
          refereeTrialDays
          rewardAmountInMinorUnits
          rewardCurrency
        }
      }
    }
  }
`)

export const EnrollInReferralProgramMutation = graphql(/* GraphQL */ `
  mutation enrollInReferralProgram($input: EnrollInReferralProgramInput!) {
    enrollInReferralProgram(input: $input) {
      __typename
      ... on EnrollInReferralProgramSuccess {
        referralProgram {
          referralUrl
          sharingStatus
          rewardSummary {
            grantedCount
            inProgressCount
            nextExpiryAt
            reconcilingCount
            remainingCount
            waitingForBillingCount
          }
          terms {
            policyVersion
            maxRewards
            refereeTrialDays
            rewardAmountInMinorUnits
            rewardCurrency
          }
        }
      }
      ... on EnrollInReferralProgramUnavailableError {
        message
      }
    }
  }
`)

export type ReferralProgramStatusResult = ResultOf<
  typeof ReferralProgramStatusQuery
>['referralProgramStatus']

type EnrolledStatus = Extract<
  ReferralProgramStatusResult,
  { __typename: 'ReferralProgramEnrolled' }
>

type ReferralProgramTerms = EnrolledStatus['terms']

function formatRewardAmount(terms: ReferralProgramTerms): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: terms.rewardCurrency,
    minimumFractionDigits: 0,
  }).format(terms.rewardAmountInMinorUnits / 100)
}

function formatRewardTotal(terms: ReferralProgramTerms, count: number): string {
  return formatRewardAmount({
    ...terms,
    rewardAmountInMinorUnits: terms.rewardAmountInMinorUnits * count,
  })
}

function formatExpiryDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

const OfferSummary = ({
  terms,
}: {
  terms: ReferralProgramTerms
}): JSX.Element => {
  return (
    <Text color="subtle">
      Your friend gets a {terms.refereeTrialDays}-day trial of an eligible paid
      Buffer.com plan. You get a {formatRewardAmount(terms)} web credit after
      their first payment passes validation, up to{' '}
      {pluralize(terms.maxRewards, 'credit')}.
    </Text>
  )
}

const CreditTerms = (): JSX.Element => {
  return (
    <Text color="subtle">
      Credit applies to future subscription invoices from Buffer.com. It does
      not apply to Apple App Store or Google Play subscriptions. You can receive
      web credit while you use the free plan or mobile billing.
    </Text>
  )
}

/**
 * Creates the link on the first visit, so the page opens on a shareable link.
 * One request per mount: a second concurrent enrollment can race the Stripe
 * gateway switch and create a second customer.
 */
const EnrollmentInProgress = ({
  organizationId,
}: {
  organizationId: string
}): JSX.Element => {
  const [enroll, { data, error }] = useMutation(
    EnrollInReferralProgramMutation,
    {
      variables: { input: { organizationId } },
      refetchQueries: [ReferralProgramStatusQuery],
      awaitRefetchQueries: true,
    },
  )
  const enrollmentRequested = useRef(false)

  const requestEnrollment = useCallback((): void => {
    enroll().catch(() => {
      // useMutation already exposes a failed enrollment through `error`.
    })
  }, [enroll])

  useEffect(() => {
    if (enrollmentRequested.current) return
    enrollmentRequested.current = true
    requestEnrollment()
  }, [requestEnrollment])

  if (error !== undefined) {
    return (
      <Flex gap="sm" direction="column" fullWidth>
        <Text>We could not create your referral link.</Text>
        <div>
          <Button size="medium" variant="secondary" onClick={requestEnrollment}>
            Try Again
          </Button>
        </div>
      </Flex>
    )
  }

  const result = data?.enrollInReferralProgram
  if (result?.__typename === 'EnrollInReferralProgramUnavailableError') {
    return <Text color="subtle">{result.message}</Text>
  }

  return <Skeleton width="100%" height="20px" />
}

/**
 * Enrollment can move an organization with no gateway to Stripe, so it needs
 * the same permissions as checkout. Other members see the offer only.
 */
const EnrollmentRestricted = ({
  terms,
}: {
  terms: ReferralProgramTerms
}): JSX.Element => {
  return (
    <Flex gap="md" direction="column" fullWidth>
      <Flex gap="sm" direction="column" fullWidth>
        <OfferSummary terms={terms} />
        <CreditTerms />
      </Flex>
      <Text color="subtle">
        Your organization does not have a referral link yet. A teammate who
        manages billing can open this page to create it.
      </Text>
    </Flex>
  )
}

const HowItWorksStep = ({
  icon,
  tone,
  title,
  children,
}: {
  icon: string
  tone: 'info' | 'warning' | 'success'
  title: string
  children: ReactNode
}): JSX.Element => {
  return (
    <li className={referAFriendStyle.step}>
      <span
        aria-hidden="true"
        className={clsx(referAFriendStyle.stepIcon, referAFriendStyle[tone])}
      >
        {icon}
      </span>
      <Flex gap="2xs" direction="column">
        <Text size="lg">{title}</Text>
        <Text color="subtle">{children}</Text>
      </Flex>
    </li>
  )
}

const HowItWorks = ({
  terms,
}: {
  terms: ReferralProgramTerms
}): JSX.Element => {
  const amount = formatRewardAmount(terms)

  return (
    <Flex gap="md" direction="column" fullWidth>
      <Heading as="h2" size="small">
        How it works
      </Heading>
      <ol className={referAFriendStyle.steps}>
        <HowItWorksStep icon="🔗" tone="info" title="1. Grab your link">
          Find the unique referral link above for your organization.
        </HowItWorksStep>
        <HowItWorksStep
          icon="💌"
          tone="warning"
          title="2. Share it with a friend"
        >
          Share your link publicly or with anyone who would enjoy using Buffer.
          When they sign up through your link, they get a{' '}
          {terms.refereeTrialDays}-day trial of an eligible paid Buffer.com
          plan.
        </HowItWorksStep>
        <HowItWorksStep
          icon="💸"
          tone="success"
          title={`3. Get ${amount} in credit`}
        >
          After your friend&apos;s first payment is confirmed, we add {amount}{' '}
          in credit to your account, up to{' '}
          {pluralize(terms.maxRewards, 'credit')}. You&apos;ll see it on your
          next Buffer.com bill.
        </HowItWorksStep>
      </ol>
    </Flex>
  )
}

const ReferralFaq = ({
  terms,
}: {
  terms: ReferralProgramTerms
}): JSX.Element => {
  const amount = formatRewardAmount(terms)
  const capAmount = formatRewardTotal(terms, terms.maxRewards)
  const questions = [
    {
      question: 'What does my friend get?',
      answer: `A ${terms.refereeTrialDays}-day trial of an eligible paid Buffer.com plan when they sign up through your link.`,
    },
    {
      question: 'When do I get my reward?',
      answer: `After your friend's first payment passes validation, we add ${amount} in web credit to your account.`,
    },
    {
      question: 'How is the reward paid out?',
      answer: `Each ${amount} credit is added to your Buffer.com account and reduces your next subscription invoice.`,
    },
    {
      question: 'How many friends can I refer?',
      answer: `As many as you like. You can earn up to ${pluralize(terms.maxRewards, 'credit')} (${capAmount} in total), and friends who use your link after that still get their trial.`,
    },
  ]

  return (
    <Flex gap="sm" direction="column" fullWidth>
      <Heading as="h2" size="small">
        Frequently asked questions
      </Heading>
      <div className={referAFriendStyle.faq}>
        {questions.map(({ question, answer }) => (
          <Collapsible key={question} className={referAFriendStyle.faqItem}>
            <Collapsible.Trigger className={referAFriendStyle.faqTrigger}>
              <span className={referAFriendStyle.faqQuestion}>{question}</span>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text
                as="p"
                color="subtle"
                className={referAFriendStyle.faqAnswer}
              >
                {answer}
              </Text>
            </Collapsible.Content>
          </Collapsible>
        ))}
      </div>
    </Flex>
  )
}

const PartnerProgramCard = (): JSX.Element => {
  return (
    <PromotionalBanner
      variant="inline"
      className={referAFriendStyle.partnerBanner}
    >
      <PromotionalBanner.Content>
        <Heading as="h2" size="small">
          Want to share Buffer at scale?
        </Heading>
        <PromotionalBanner.Description color="subtle">
          Our Partner Program pays a 25% commission on every customer you refer,
          for 12 months. Made for creators, agencies, and communities.
        </PromotionalBanner.Description>
        <PromotionalBanner.Actions>
          <Button
            as="a"
            href="https://buffer.com/partners"
            target="_blank"
            rel="noopener"
            size="medium"
            variant="primary"
          >
            Learn more
          </Button>
        </PromotionalBanner.Actions>
      </PromotionalBanner.Content>
      <PromotionalBanner.Image
        src="https://buffer-publish.s3.us-east-1.amazonaws.com/images/partner-program.png"
        fallbackColor="#D5DBFF"
        className={referAFriendStyle.partnerImage}
      />
    </PromotionalBanner>
  )
}

type RewardSlotState =
  | 'granted'
  | 'inProgress'
  | 'waitingForBilling'
  | 'reconciling'
  | 'open'
  | 'paused'

type RewardSlot = { position: number; state: RewardSlotState }

function toRewardSlots(
  summary: EnrolledStatus['rewardSummary'],
  sharingAvailable: boolean,
): RewardSlot[] {
  const states: RewardSlotState[] = [
    ...Array<RewardSlotState>(summary.grantedCount).fill('granted'),
    ...Array<RewardSlotState>(summary.waitingForBillingCount).fill(
      'waitingForBilling',
    ),
    ...Array<RewardSlotState>(summary.inProgressCount).fill('inProgress'),
    ...Array<RewardSlotState>(summary.reconcilingCount).fill('reconciling'),
    ...Array<RewardSlotState>(summary.remainingCount).fill(
      sharingAvailable ? 'open' : 'paused',
    ),
  ]
  return states.map((state, index) => ({ position: index + 1, state }))
}

const rewardSlotProgressClass: Record<RewardSlotState, string | undefined> = {
  granted: referAFriendStyle.credited,
  waitingForBilling: referAFriendStyle.onTheWay,
  inProgress: referAFriendStyle.onTheWay,
  reconciling: referAFriendStyle.onTheWay,
  open: undefined,
  paused: undefined,
}

const rewardSlotStatus: Record<
  RewardSlotState,
  { label: string; description: string }
> = {
  granted: { label: 'Credited', description: 'Added to your billing' },
  waitingForBilling: {
    label: 'Earned',
    description: 'Applies when you start a paid plan',
  },
  inProgress: {
    label: 'Confirming payment',
    description: 'A friend made their first payment',
  },
  reconciling: { label: 'Updating', description: 'Check back soon' },
  open: { label: 'Available', description: 'Share your link to earn it' },
  paused: { label: 'Paused', description: 'Your link is paused' },
}

function creditProgressSummary(status: EnrolledStatus): string {
  const { rewardSummary, terms } = status
  const pendingCount =
    rewardSummary.inProgressCount +
    rewardSummary.waitingForBillingCount +
    rewardSummary.reconcilingCount
  const sharingAvailable = status.sharingStatus === 'available'
  const sentences: string[] = []

  if (pendingCount > 0) {
    sentences.push(`${formatRewardTotal(terms, pendingCount)} on the way.`)
  }
  if (rewardSummary.remainingCount > 0) {
    if (sharingAvailable) {
      sentences.push(
        `Earn ${formatRewardAmount(terms)} with your next referral.`,
      )
    }
  } else {
    if (rewardSummary.grantedCount === terms.maxRewards) {
      sentences.push(
        `You have received all ${pluralize(terms.maxRewards, 'credit')}.`,
      )
    }
    if (sharingAvailable) {
      sentences.push(
        `Friends who use your link still get their ${terms.refereeTrialDays}-day trial.`,
      )
    }
  }
  return sentences.join(' ')
}

const CreditProgress = ({
  status,
}: {
  status: EnrolledStatus
}): JSX.Element => {
  const { rewardSummary, terms } = status
  const { nextExpiryAt, waitingForBillingCount } = rewardSummary
  const slots = toRewardSlots(
    rewardSummary,
    status.sharingStatus === 'available',
  )
  const summary = creditProgressSummary(status)

  return (
    <Flex gap="md" direction="column" fullWidth>
      <div className={referAFriendStyle.creditProgress}>
        <Flex gap="2xs" direction="column">
          <Heading as="h2" size="small">
            Your credits
          </Heading>
          <Heading as="p" size="large">
            {formatRewardTotal(terms, rewardSummary.grantedCount)} of{' '}
            {formatRewardTotal(terms, terms.maxRewards)} received
          </Heading>
          {summary && <Text color="subtle">{summary}</Text>}
        </Flex>
        <ol className={referAFriendStyle.creditSteps}>
          {slots.map(({ position, state }) => (
            <li
              key={position}
              className={clsx(
                referAFriendStyle.creditStep,
                rewardSlotProgressClass[state],
              )}
            >
              <span
                aria-hidden="true"
                className={referAFriendStyle.creditMarker}
              >
                {state === 'granted' ? <CheckIcon size="small" /> : position}
              </span>
              <Text weight="medium">
                <VisuallyHidden>Credit {position}: </VisuallyHidden>
                {rewardSlotStatus[state].label}
              </Text>
              <Text color="subtle" size="sm">
                {rewardSlotStatus[state].description}
              </Text>
            </li>
          ))}
        </ol>
        <Text color="subtle" size="sm">
          Friends appear here after their first payment.
        </Text>
      </div>
      {waitingForBillingCount > 0 && nextExpiryAt && (
        <Notice variant="warning">
          <Notice.Text>
            Start a paid Buffer.com plan by {formatExpiryDate(nextExpiryAt)} to
            keep your {waitingForBillingCount === 1 ? '' : 'earliest '}earned
            credit.
          </Notice.Text>
        </Notice>
      )}
    </Flex>
  )
}

const EnrolledSection = ({
  organizationId,
  status,
}: {
  organizationId: string
  status: EnrolledStatus
}): JSX.Element => {
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()
  const sharingAvailable = status.sharingStatus === 'available'

  const handleCopyLink = (): void => {
    navigator.clipboard
      .writeText(status.referralUrl)
      .then(() => {
        BufferTracker.referralLinkCopied({ organizationId })
        return toast.success('Referral link copied to clipboard')
      })
      .catch(() => {
        return toast.error('Failed to copy referral link')
      })
  }

  const handleNativeShare = async (): Promise<void> => {
    try {
      await navigator.share({ title: 'Try Buffer', url: status.referralUrl })
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        toast.error(
          'Could not share your referral link. Try copying it instead.',
        )
      }
      return
    }
    BufferTracker.referralLinkShared({
      organizationId,
      clientName: 'publishWeb',
      policyVersion: status.terms.policyVersion,
    })
  }

  const handleShareNow = useCallback((): void => {
    BufferTracker.referralPostStarted({ organizationId })

    void createNewPostInComposer({
      cta: 'publish-settings-referAFriend-shareNow-1',
      channels: 'all',
      prefillPostData: {
        text: `I’m using Buffer to grow an engaged audience on social and beyond. Give it a try and start your free trial today: ${status.referralUrl}`,
      },
    })
  }, [createNewPostInComposer, organizationId, status.referralUrl])

  return (
    <Flex gap="xl" direction="column" fullWidth>
      <Flex gap="md" direction="column" fullWidth>
        <CreditProgress status={status} />
        <div className={referAFriendStyle.linkRow}>
          <Text weight="medium" className={referAFriendStyle.referralLink}>
            {status.referralUrl}
          </Text>
          <div className={referAFriendStyle.linkActions}>
            <Button
              size="small"
              variant="secondary"
              onClick={handleCopyLink}
              disabled={!sharingAvailable}
              aria-label="Copy Link"
            >
              <CopyIcon />
              Copy
            </Button>
            {typeof navigator.share === 'function' && (
              <IconButton
                label="Share via Device"
                tooltip="Share via Device"
                size="small"
                variant="secondary"
                onClick={handleNativeShare}
                disabled={!sharingAvailable}
              >
                <Share2Icon />
              </IconButton>
            )}
            <Button
              {...triggerAttributes}
              size="small"
              variant="primary"
              onClick={handleShareNow}
              disabled={!sharingAvailable}
            >
              <SendHorizontalIcon />
              Share Now
            </Button>
          </div>
        </div>
        {!sharingAvailable && (
          <Text color="subtle">
            Your link is not accepting new referrals right now.
          </Text>
        )}
      </Flex>
      {sharingAvailable && <HowItWorks terms={status.terms} />}
      <CreditTerms />
      <ReferralFaq terms={status.terms} />
    </Flex>
  )
}

const ReferralProgramContent = ({
  organizationId,
  fetchPolicy,
}: {
  organizationId: string
  fetchPolicy: 'cache-first' | 'network-only'
}): JSX.Element => {
  const canEdit = useHasOrganizationAction('edit')
  const canManageBilling = useHasOrganizationAction('manageBilling')
  const canEnroll = canEdit && canManageBilling
  const { data, loading, error, refetch } = useQuery(
    ReferralProgramStatusQuery,
    {
      variables: { input: { organizationId } },
      fetchPolicy,
    },
  )

  const handleRetry = (): void => {
    refetch().catch(() => {
      // useQuery already exposes a failed refetch through `error`.
    })
  }

  // Enrollment refetches this query. Keeping the settled view mounted during
  // that refetch stops `EnrollmentInProgress` from remounting and enrolling
  // again.
  if (loading && data === undefined) {
    return <Skeleton width="100%" height="20px" />
  }

  if (error !== undefined || data === undefined) {
    return (
      <Flex gap="sm" direction="column" fullWidth>
        <Text>We could not load your referral program status.</Text>
        <div>
          <Button size="medium" variant="secondary" onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </Flex>
    )
  }

  const status = data.referralProgramStatus
  switch (status.__typename) {
    case 'ReferralProgramUnavailable':
      return <Text color="subtle">{status.message}</Text>
    case 'ReferralProgramUnenrolled':
      return canEnroll ? (
        <EnrollmentInProgress organizationId={organizationId} />
      ) : (
        <EnrollmentRestricted terms={status.terms} />
      )
    case 'ReferralProgramEnrolled':
      return <EnrolledSection organizationId={organizationId} status={status} />
  }
}

export const ReferralProgramPage = ({
  fetchPolicy = 'cache-first',
}: {
  fetchPolicy?: 'cache-first' | 'network-only'
}): JSX.Element => {
  const { account } = useAccount()
  const organizationId = account?.currentOrganization?.id

  return (
    <NewSettingsPageLayout
      title="Refer a Friend"
      subtitle="Earn Buffer credit when friends you refer become paying customers."
      gap="xl"
    >
      <section className={style.fullWidth}>
        {organizationId === undefined ? (
          <Skeleton width="100%" height="20px" />
        ) : (
          <ReferralProgramContent
            organizationId={organizationId}
            fetchPolicy={fetchPolicy}
          />
        )}
      </section>
      <section className={style.fullWidth}>
        <PartnerProgramCard />
      </section>
    </NewSettingsPageLayout>
  )
}
